<script setup lang="ts">
import { getCountryCallingCode, parseIncompletePhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import Dropdown from "primevue/dropdown";
import InputMask from "primevue/inputmask";
import { computed, defineEmits, ref, toRefs, watch } from "vue";
import { US, phoneMaskMap } from "./constants.ts";
import { pt } from "./styles.ts";
import type { Props } from "./types.ts";
import { getCountryOptions } from "./utils";
import "/node_modules/flag-icons/css/flag-icons.min.css";

const props = defineProps<Props>();
const { modelValue, invalid } = toRefs(props);

const selectedCountry = ref<CountryCode>(US);
const internalPhoneNumber = ref("");

const emit = defineEmits(["change", "blur", "update:modelValue"]);

const countryOptions = getCountryOptions();

const phoneMask = computed(() => {
  return phoneMaskMap[selectedCountry.value] ?? phoneMaskMap.UNK;
});

watch(
  modelValue,
  (newNumber, oldNumber) => {
    const cleanedNewNumber = newNumber?.replace(/\D/g, "") ?? null;
    if (cleanedNewNumber) {
      const parsedNewNumber = parsePhoneNumberFromString(`+${cleanedNewNumber ?? ""}`);
      if (parsedNewNumber) {
        const possibleCountryCodes = parsedNewNumber.getPossibleCountries();
        const newCountryCode = possibleCountryCodes.length > 0 ? possibleCountryCodes[0] : selectedCountry.value || US;
        const newNationalNumber = `${parsedNewNumber.nationalNumber}`;
        if (newCountryCode !== selectedCountry.value) {
          selectedCountry.value = newCountryCode;
        }
        const cleanedInternalPhoneNumber = internalPhoneNumber.value?.replace(/\D/g, "") ?? null;
        if (newNationalNumber !== cleanedInternalPhoneNumber) {
          internalPhoneNumber.value = newNationalNumber;
        }
      }
    }
  },
  { immediate: true },
);

watch([selectedCountry, internalPhoneNumber], ([newCountry, newNumber], [oldCountry, oldNumber]) => {
  const cleanedNewNumber = newNumber?.replace(/\D/g, "") ?? null;
  const cleanedOldNumber = oldNumber?.replace(/\D/g, "") ?? null;

  if (newCountry !== oldCountry || cleanedNewNumber !== cleanedOldNumber) {
    const newCountryCode = getCountryCallingCode(newCountry);
    const assembledNumber = `+${newCountryCode}${cleanedNewNumber}`;
    const parsedAssembledNumber = parsePhoneNumberFromString(assembledNumber);
    const assembledIncompleteNumber = `${newCountryCode}${cleanedNewNumber}`;
    const parsedIncompleteAssembledNumber = parseIncompletePhoneNumber(assembledIncompleteNumber);
    emit("change", {
      nationalNumber: parsedAssembledNumber?.nationalNumber,
      countryCode: newCountryCode,
      wholeNumber: assembledNumber,
    });
    emit("update:modelValue", `+${parsedIncompleteAssembledNumber}`);
  }
});
</script>

<template>
  <div class="flex justify-content-center">
    <Dropdown
      v-model="selectedCountry"
      :options="countryOptions"
      option-label="label"
      option-value="id"
      class="w-40 pr-2 rounded-none rounded-l-lg border-r-0"
      :pt="pt"
      :invalid="invalid"
      @blur="event => emit('blur', event)"
    >
      <template v-slot:value="{ value }">
        <div class="text-xs pt-2 pb-1">Country Code</div>
        <div class="pb-1"><span :class="`mr-2 fi fi-${value?.toLowerCase()}`"></span> +{{ getCountryCallingCode(value) }}</div>
      </template>
      <template v-slot:option="{option, index}">
        <div class="pb-1"><span :class="`mr-2 fi fi-${option?.id?.toLowerCase()}`"></span>{{ option?.label }}--{{ option?.id.toLowerCase() }}</div>
      </template>
    </Dropdown>
    <InputMask
      v-model="internalPhoneNumber"
      :mask="phoneMask"
      :autoClear="false"
      placeholder="Phone Number"
      class="w-full rounded-none rounded-r-lg"
      :invalid="invalid"
      @blur="event => emit('blur', event)"
    />
  </div>
</template>

<style scoped>

</style>
